import { useEffect, useState } from 'react';

import SoftSnackbar from 'components/SoftSnackbar';

type SuiSnackbarColor =
  | 'primary'
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'error'
  | 'dark'
  | 'light';

interface SuiSnackbarMessage {
  message: string;
  color?: SuiSnackbarColor;
  title?: string;
  icon?: string;
  dateTime?: string;
  durationMs?: number;
}

interface ActiveSuiSnackbarMessage {
  message: string;
  color: SuiSnackbarColor;
  title: string;
  icon: string;
  dateTime: string;
  durationMs: number;
}

type SnackbarListener = (message: SuiSnackbarMessage) => void;

const snackbarListeners = new Set<SnackbarListener>();
const DEDUP_WINDOW_MS = 350;
const DEFAULT_SNACKBAR_DURATION_MS = 10000;
let lastBroadcastKey = '';
let lastBroadcastAt = 0;

function normalizeMessage(message: SuiSnackbarMessage): SuiSnackbarMessage | null {
  const normalizedText = message.message.trim();
  if (!normalizedText) {
    return null;
  }

  return {
    ...message,
    message: normalizedText,
  };
}

function formatTimestamp(dateTime?: string): string {
  if (typeof dateTime === 'string' && dateTime.trim()) {
    return dateTime.trim();
  }

  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date());
}

function toActiveMessage(message: SuiSnackbarMessage): ActiveSuiSnackbarMessage {
  const color = message.color ?? 'info';

  return {
    message: message.message,
    color,
    title: message.title?.trim() || (color === 'error' ? 'Error' : 'Notice'),
    icon: message.icon?.trim() || (color === 'error' ? 'error_outline' : 'notifications'),
    dateTime: formatTimestamp(message.dateTime),
    durationMs:
      message.durationMs && message.durationMs > 0
        ? message.durationMs
        : DEFAULT_SNACKBAR_DURATION_MS,
  };
}

function broadcastSnackbarMessage(message: SuiSnackbarMessage): void {
  const normalizedMessage = normalizeMessage(message);
  if (!normalizedMessage) {
    return;
  }

  const dedupKey = `${normalizedMessage.color ?? 'info'}:${normalizedMessage.message}`;
  const now = Date.now();
  if (lastBroadcastKey === dedupKey && now - lastBroadcastAt <= DEDUP_WINDOW_MS) {
    return;
  }

  lastBroadcastKey = dedupKey;
  lastBroadcastAt = now;

  snackbarListeners.forEach((listener) => listener(normalizedMessage));
}

function subscribeToSuiSnackbar(listener: SnackbarListener): () => void {
  snackbarListeners.add(listener);
  return () => {
    snackbarListeners.delete(listener);
  };
}

export function showSuiSnackbar(message: SuiSnackbarMessage): void {
  broadcastSnackbarMessage(message);
}

export function showSuiErrorSnackbar(
  message: string,
  options?: Omit<SuiSnackbarMessage, 'message' | 'color'>
): void {
  broadcastSnackbarMessage({
    ...options,
    message,
    color: 'error',
  });
}

export function showSuiSuccessSnackbar(
  message: string,
  options?: Omit<SuiSnackbarMessage, 'message' | 'color'>
): void {
  broadcastSnackbarMessage({
    ...options,
    message,
    color: 'success',
    title: options?.title ?? 'Success',
    icon: options?.icon ?? 'check_circle',
  });
}

export function SuiSnackbarProvider() {
  const [queue, setQueue] = useState<ActiveSuiSnackbarMessage[]>([]);
  const [activeSnackbar, setActiveSnackbar] = useState<ActiveSuiSnackbarMessage | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(
    () =>
      subscribeToSuiSnackbar((message) => {
        setQueue((currentQueue) => [...currentQueue, toActiveMessage(message)]);
      }),
    []
  );

  useEffect(() => {
    if (activeSnackbar || queue.length === 0) {
      return;
    }

    const [nextSnackbar, ...remainingQueue] = queue;
    setActiveSnackbar(nextSnackbar);
    setQueue(remainingQueue);
    setIsOpen(true);
  }, [activeSnackbar, queue]);

  useEffect(() => {
    if (!activeSnackbar || !isOpen) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setIsOpen(false);
    }, activeSnackbar.durationMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [activeSnackbar, isOpen]);

  const handleClose = (_event?: unknown, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setIsOpen(false);
  };

  const handleExited = () => {
    setActiveSnackbar(null);
  };

  if (!activeSnackbar) {
    return null;
  }

  return (
    <SoftSnackbar
      color={activeSnackbar.color}
      icon={activeSnackbar.icon}
      title={activeSnackbar.title}
      dateTime={activeSnackbar.dateTime}
      content={activeSnackbar.message}
      open={isOpen}
      close={handleClose}
      onClose={handleClose}
      autoHideDuration={activeSnackbar.durationMs}
      TransitionProps={{ onExited: handleExited }}
    />
  );
}
